import { useCallback, useEffect, useState } from "react";
import { Loader2, Trash2, CalendarDays, Copy, Undo2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import AdminBookingControls from "./AdminBookingControls";

const SERVICE_LABELS = {
  tier: "Tierbetreuung",
  design: "Clothing & Design",
  brand: "Clothing Brand Aufbau",
  autosuche: "Autosuche",
};

const fmtEur = (cents) => (cents / 100).toLocaleString("de-DE", { style: "currency", currency: "EUR" });

const STATUS_STYLE = {
  offen: "border-goldlight/50 text-goldlight bg-goldlight/5",
  bezahlt: "border-emerald-600/40 text-emerald-400 bg-emerald-600/5",
  storniert: "border-red-500/40 text-red-400 bg-red-500/5",
  erstattet: "border-sky-500/40 text-sky-400 bg-sky-500/5",
};

const STATUS_LABEL = { offen: "Zahlung offen", bezahlt: "Bezahlt", storniert: "Storniert", erstattet: "Erstattet" };

export default function AdminBookings() {
  const [bookings, setBookings] = useState(undefined);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/bookings");
      setBookings(data);
    } catch (err) {
      toast.error(formatApiError(err));
      setBookings([]);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const remove = async (id) => {
    if (!window.confirm("Buchung wirklich löschen?")) return;
    await api.delete(`/bookings/${id}`);
    toast.success("Buchung gelöscht");
    load();
  };

  const cancel = async (id) => {
    await api.patch(`/bookings/${id}`, { status: "storniert" });
    toast.success("Buchung storniert");
    load();
  };

  const refund = async (b) => {
    if (!window.confirm(`Erstattung für ${b.number} über ${fmtEur(b.price_cents)} wirklich auslösen?\n\nDas Geld geht automatisch an den Kunden zurück. Das kann nicht rückgängig gemacht werden.`)) return;
    try {
      await api.post(`/bookings/${b.id}/refund`);
      toast.success("Erstattung ausgelöst — der Kunde wurde per E-Mail informiert.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const copyLink = (id) => {
    const link = `https://justinhilft.de/buchung/${id}`;
    navigator.clipboard.writeText(link).then(() => toast.success("Buchungs-Link kopiert — per WhatsApp/SMS an den Kunden schicken.")).catch(() => toast.info(link));
  };

  if (bookings === undefined) {
    return (
      <div className="flex items-center gap-3 text-zinc-500 py-20 justify-center">
        <Loader2 size={18} className="animate-spin" /> Lade Buchungen ...
      </div>
    );
  }

  if (bookings.length === 0) {
    return (
      <div>
        <AdminBookingControls />
        <div className="border border-dashed border-white/15 p-16 text-center" data-testid="bookings-empty-state">
          <p className="font-display text-xl text-zinc-400 mb-2">Noch keine Buchungen</p>
          <p className="text-sm text-zinc-600">Sobald ein Kunde eine Festpreis-Leistung bucht und bezahlt, erscheint sie hier.</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AdminBookingControls />
      <ul className="space-y-4" data-testid="bookings-list">
      {bookings.map((b) => (
        <li key={b.id} className="border border-white/10 bg-white/[0.02] p-5 lg:p-6" data-testid={`booking-row-${b.number}`}>
          <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
            <div>
              <p className="font-display text-lg text-bone">{b.number}</p>
              <p className="text-xs text-zinc-500 mt-1">
                {new Date(b.created_at).toLocaleDateString("de-DE")} · {SERVICE_LABELS[b.service] || b.service}
              </p>
            </div>
            <span className={`border px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] ${STATUS_STYLE[b.status] || STATUS_STYLE.offen}`} data-testid={`booking-status-${b.number}`}>
              {STATUS_LABEL[b.status] || b.status}
            </span>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Kunde</p>
              <p className="text-zinc-200">{b.name}</p>
              <p className="text-zinc-500 text-xs">{b.email}{b.phone ? ` · ${b.phone}` : ""}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Paket / Leistung</p>
              <p className="text-zinc-200">{b.item_label}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Preis</p>
              <p className="font-display text-xl text-goldlight" data-testid={`booking-price-${b.number}`}>{fmtEur(b.price_cents)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1">Termin</p>
              {b.date ? (
                <p className="text-zinc-200 flex items-center gap-2">
                  <CalendarDays size={14} className="text-goldlight" />
                  {b.date.split("-").reverse().join(".")} · {b.start_time}–{b.end_time || "?"} Uhr
                </p>
              ) : (
                <p className="text-zinc-500">—</p>
              )}
            </div>
          </div>
          {(b.pet_info || b.notes) && (
            <div className="mt-4 pt-4 border-t border-white/5 space-y-2 text-sm">
              {b.pet_info && <p className="text-zinc-400"><span className="text-zinc-500">Tier:</span> {b.pet_info}</p>}
              {b.notes && <p className="text-zinc-400"><span className="text-zinc-500">Anmerkungen:</span> {b.notes}</p>}
            </div>
          )}
          <div className="flex flex-wrap gap-3 mt-5">
            <button
              onClick={() => copyLink(b.id)}
              data-testid={`booking-copy-${b.id}`}
              className="text-[11px] uppercase tracking-[0.2em] text-goldlight border border-goldlight/40 px-4 py-2 transition-colors duration-300 hover:bg-goldlight hover:text-coal flex items-center gap-2"
            >
              <Copy size={13} /> Zahlungs-Link kopieren
            </button>
            {b.status === "bezahlt" && b.paid_method === "stripe" && (
              <button
                onClick={() => refund(b)}
                data-testid={`booking-refund-${b.id}`}
                className="text-[11px] uppercase tracking-[0.2em] text-sky-400 border border-sky-500/40 px-4 py-2 transition-colors duration-300 hover:bg-sky-500 hover:text-white flex items-center gap-2"
              >
                <Undo2 size={13} /> Erstatten
              </button>
            )}
            {b.status !== "storniert" && b.status !== "erstattet" && (
              <button
                onClick={() => cancel(b.id)}
                data-testid={`booking-cancel-${b.id}`}
                className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 border border-white/10 hover:border-red-500/40 px-4 py-2 transition-colors duration-300"
              >
                Stornieren
              </button>
            )}
            <button
              onClick={() => remove(b.id)}
              data-testid={`booking-delete-${b.id}`}
              className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 hover:text-red-400 border border-white/10 hover:border-red-500/40 px-4 py-2 transition-colors duration-300 flex items-center gap-2"
            >
              <Trash2 size={13} /> Löschen
            </button>
          </div>
        </li>
      ))}
      </ul>
    </div>
  );
}
